import { Autocomplete, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useAdapterConfigLabels } from '../../hooks/useAdapterServices';
import { configLabelFromAutocompleteValue } from './configLabelSelectValue';

type Props = {
  value: string;
  onChange: (label: string) => void;
  disabled?: boolean;
  helperText?: string;
  /** When true, only existing labels can be selected; empty value is allowed. */
  selectOnly?: boolean;
  /** When false, omit inline helper text (e.g. when parent renders a shared caption). */
  showHelperText?: boolean;
};

export { configLabelFromAutocompleteValue } from './configLabelSelectValue';

const DEFAULT_HELPER_TEXT =
  'Namespace in adapter_configs.label — publish and sync vendor config under this label.';

export function ConfigLabelSelect({
  value,
  onChange,
  disabled,
  helperText,
  selectOnly = false,
  showHelperText = true,
}: Props) {
  const { data: labels = ['default'], isLoading } = useAdapterConfigLabels();
  const options = selectOnly || labels.includes(value) || !value ? labels : [...labels, value];

  if (selectOnly) {
    const labelId = 'adapter-config-label-select';
    return (
      <FormControl fullWidth required disabled={disabled || isLoading}>
        <InputLabel id={labelId}>Adapter config label</InputLabel>
        <Select
          labelId={labelId}
          value={value}
          label="Adapter config label"
          onChange={e => onChange(configLabelFromAutocompleteValue(true, String(e.target.value)))}
        >
          {options.map(label => (
            <MenuItem key={label} value={label}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <Autocomplete
      fullWidth
      freeSolo
      disabled={disabled}
      loading={isLoading}
      options={options}
      value={value}
      onChange={(_, v) => onChange(configLabelFromAutocompleteValue(false, v))}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') onChange(v.trim() || 'default');
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Adapter config label"
          helperText={
            showHelperText ? (helperText ?? DEFAULT_HELPER_TEXT) : undefined
          }
          required
        />
      )}
    />
  );
}
